import type {
  IBrandQuoteThemeColorPalette,
  IBrandQuoteThemeConfig,
  IBrandQuoteThemeFontGroup,
} from "@/@sdk/brands/brand-model";

export const ALLOWED_QUOTE_GOOGLE_FONTS = [
  "DM Sans",
  "Inter",
  "Playfair Display",
  "Source Sans 3",
  "Fraunces",
  "Nunito Sans",
  "Cormorant Garamond",
  "Lato",
  "Space Grotesk",
  "IBM Plex Sans",
  "Dancing Script",
  "Great Vibes",
  "Quicksand",
] as const;

export interface QuoteColorPalettePreset extends IBrandQuoteThemeColorPalette {
  id: string;
  name: string;
  /** Featured palettes show by default; extended palettes appear after expanding. */
  pickerTier?: "featured" | "extended";
}

export interface QuoteFontGroupPreset extends IBrandQuoteThemeFontGroup {
  id: string;
  name: string;
  schema: "SINGLE_FONT" | "DUAL_FONT";
}

export const QUOTE_COLOR_PALETTE_PRESETS: QuoteColorPalettePreset[] = [
  {
    id: "brand-primary",
    name: "Use brand color",
    presetId: "brand-primary",
    primary: "#F15933",
    secondary: "#FFF5F2",
    pickerTier: "featured",
  },
  {
    id: "ocean-trust",
    name: "Ocean Trust",
    presetId: "ocean-trust",
    primary: "#0E7490",
    secondary: "#F0FDFA",
    pickerTier: "featured",
  },
  {
    id: "sunset-travel",
    name: "Sunset Travel",
    presetId: "sunset-travel",
    primary: "#EA580C",
    secondary: "#FFF7ED",
    pickerTier: "featured",
  },
  {
    id: "forest-escape",
    name: "Forest Escape",
    presetId: "forest-escape",
    primary: "#15803D",
    secondary: "#F0FDF4",
    pickerTier: "featured",
  },
  {
    id: "royal-premium",
    name: "Royal Premium",
    presetId: "royal-premium",
    primary: "#4C1D95",
    secondary: "#EDE9FE",
    pickerTier: "featured",
  },
  {
    id: "midnight-gold",
    name: "Midnight Gold",
    presetId: "midnight-gold",
    primary: "#1E293B",
    secondary: "#FEF3C7",
    pickerTier: "featured",
  },
  {
    id: "ocean-coral",
    name: "Ocean & Coral",
    presetId: "ocean-coral",
    primary: "#0369A1",
    secondary: "#FDA4AF",
    pickerTier: "featured",
  },
  {
    id: "plum-mint",
    name: "Plum & Mint",
    presetId: "plum-mint",
    primary: "#6B21A8",
    secondary: "#99F6E4",
    pickerTier: "featured",
  },
  {
    id: "slate-modern",
    name: "Slate Modern",
    presetId: "slate-modern",
    primary: "#334155",
    secondary: "#F8FAFC",
    pickerTier: "extended",
  },
  {
    id: "coral-energy",
    name: "Coral Energy",
    presetId: "coral-energy",
    primary: "#F15933",
    secondary: "#FFF5F2",
    pickerTier: "extended",
  },
  {
    id: "navy-peach",
    name: "Navy & Peach",
    presetId: "navy-peach",
    primary: "#1E3A8A",
    secondary: "#FDBA74",
    pickerTier: "extended",
  },
  {
    id: "wine-cream",
    name: "Wine & Cream",
    presetId: "wine-cream",
    primary: "#881337",
    secondary: "#FEF9C3",
    pickerTier: "extended",
  },
  {
    id: "forest-amber",
    name: "Forest & Amber",
    presetId: "forest-amber",
    primary: "#166534",
    secondary: "#FDE047",
    pickerTier: "extended",
  },
  {
    id: "teal-rose",
    name: "Teal & Rose",
    presetId: "teal-rose",
    primary: "#0F766E",
    secondary: "#FDA4AF",
    pickerTier: "extended",
  },
  {
    id: "indigo-citrus",
    name: "Indigo & Citrus",
    presetId: "indigo-citrus",
    primary: "#3730A3",
    secondary: "#FEF08A",
    pickerTier: "extended",
  },
  {
    id: "slate-lavender",
    name: "Slate & Lavender",
    presetId: "slate-lavender",
    primary: "#475569",
    secondary: "#DDD6FE",
    pickerTier: "extended",
  },
  {
    id: "charcoal-rose",
    name: "Charcoal & Rose",
    presetId: "charcoal-rose",
    primary: "#3F3F46",
    secondary: "#FECDD3",
    pickerTier: "extended",
  },
  {
    id: "berry-blush",
    name: "Berry & Blush",
    presetId: "berry-blush",
    primary: "#BE185D",
    secondary: "#FBCFE8",
    pickerTier: "extended",
  },
  {
    id: "rust-olive",
    name: "Rust & Olive",
    presetId: "rust-olive",
    primary: "#C2410C",
    secondary: "#D9F99D",
    pickerTier: "extended",
  },
];

export const FEATURED_QUOTE_COLOR_PALETTE_PRESETS =
  QUOTE_COLOR_PALETTE_PRESETS.filter(
    (preset) => preset.pickerTier !== "extended"
  );

export const EXTENDED_QUOTE_COLOR_PALETTE_PRESETS =
  QUOTE_COLOR_PALETTE_PRESETS.filter(
    (preset) => preset.pickerTier === "extended"
  );

export const QUOTE_FONT_GROUP_PRESETS: QuoteFontGroupPreset[] = [
  {
    id: "modern-clean",
    name: "Modern Clean",
    presetId: "modern-clean",
    headingFont: "DM Sans",
    bodyFont: "Inter",
    schema: "DUAL_FONT",
  },
  {
    id: "classic-professional",
    name: "Classic Professional",
    presetId: "classic-professional",
    headingFont: "Playfair Display",
    bodyFont: "Source Sans 3",
    schema: "DUAL_FONT",
  },
  {
    id: "warm-friendly",
    name: "Warm Friendly",
    presetId: "warm-friendly",
    headingFont: "Fraunces",
    bodyFont: "Nunito Sans",
    schema: "DUAL_FONT",
  },
  {
    id: "editorial-luxury",
    name: "Editorial Luxury",
    presetId: "editorial-luxury",
    headingFont: "Cormorant Garamond",
    bodyFont: "Lato",
    schema: "DUAL_FONT",
  },
  {
    id: "tech-forward",
    name: "Tech Forward",
    presetId: "tech-forward",
    headingFont: "Space Grotesk",
    bodyFont: "IBM Plex Sans",
    schema: "DUAL_FONT",
  },
  {
    id: "curly-charm",
    name: "Curly Charm",
    presetId: "curly-charm",
    headingFont: "Dancing Script",
    bodyFont: "Lato",
    schema: "DUAL_FONT",
  },
  {
    id: "wedding-elegance",
    name: "Wedding Elegance",
    presetId: "wedding-elegance",
    headingFont: "Great Vibes",
    bodyFont: "Cormorant Garamond",
    schema: "DUAL_FONT",
  },
  {
    id: "family-together",
    name: "Family Together",
    presetId: "family-together",
    headingFont: "Quicksand",
    bodyFont: "Nunito Sans",
    schema: "DUAL_FONT",
  },
  {
    id: "uniform-inter",
    name: "Uniform Inter",
    presetId: "uniform-inter",
    headingFont: "Inter",
    bodyFont: "Inter",
    schema: "SINGLE_FONT",
  },
  {
    id: "uniform-dm-sans",
    name: "Uniform DM Sans",
    presetId: "uniform-dm-sans",
    headingFont: "DM Sans",
    bodyFont: "DM Sans",
    schema: "SINGLE_FONT",
  },
];

const DEFAULT_PRIMARY_COLOR = "#F15933";

export function getDefaultQuoteThemeConfig(
  primaryColor?: string
): IBrandQuoteThemeConfig {
  return {
    colorSchema: "SINGLE_TONE",
    colorPalette: {
      presetId: "brand-primary",
      primary: primaryColor ?? DEFAULT_PRIMARY_COLOR,
    },
    fontSchema: "SINGLE_FONT",
    fontGroup: {
      presetId: "uniform-inter",
      headingFont: "Inter",
      bodyFont: "Inter",
    },
    uiStyle: "STYLE_A",
    layoutConfig: "CONFIG_A",
    navBarTemplate: "TYPE_2",
    primaryColorFillMode: "Fill",
    headerTemplate: "Default",
  };
}

export function getEffectiveQuoteThemeConfig(
  quoteThemeConfig: IBrandQuoteThemeConfig | undefined | null,
  brandPrimaryColor?: string
): IBrandQuoteThemeConfig {
  if (!quoteThemeConfig) {
    return getDefaultQuoteThemeConfig(brandPrimaryColor);
  }

  if (
    quoteThemeConfig.colorPalette.presetId === "brand-primary" &&
    brandPrimaryColor
  ) {
    return {
      ...quoteThemeConfig,
      colorPalette: {
        ...quoteThemeConfig.colorPalette,
        primary: brandPrimaryColor,
      },
    };
  }

  return quoteThemeConfig;
}
